import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { StateService, TransitionService } from '@uirouter/angular';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';

import { UserService } from '../../../core/services/core.user.service';
import { WikiResolvesService } from '../../../plugins/wiki/wiki-resolves.service';
import { WikiService } from '../services/wiki.service';
import { WikiEditViewComponent } from '../../../plugins/wiki/edit/wiki.edit-view.component';
import { WikiDeleteModalComponent } from '../wiki-delete-modal/wiki.delete.modal.component';
import { WikiRenameModalComponent } from '../wiki-rename-modal/wiki.rename.modal.component';
import { marker } from '../../../core/services/ngx-translate-extract-marker';

/** Right-side control buttons the container renders. */
export interface WikiContainerControls {
    delete?: boolean;
    lock?: boolean;
    public?: boolean;
    monitor?: boolean;
    browse?: boolean;
    /** Offer the PDF/Word download. A read capability — see WikiPageComponent.controls. */
    download?: boolean;
    /** State the Browse button / post-delete navigation targets. */
    browseState?: string;
}

/**
 * Self-contained, drop-in wiki surface.
 *
 * A host embeds `<wiki-container [baseState]="X" [tabs]="[...]" [pagename]="…" [project]="…">` at
 * some UI-Router state `X` and declares empty substates `X.view` / `X.edit` / `X.versions` (and
 * optionally `.monitor` / `.referrers` / `.attachments`) purely as URL markers. The rest of the app
 * stays wiki-agnostic.
 *
 * The container:
 *  - fetches its own wiki data (page, lock, counts) from `project` + `pagename`;
 *  - renders the tab bar + control buttons, and the active tab's content itself (ngSwitch) — no
 *    nested `<ui-view>`, so nothing is torn down on navigation;
 *  - derives the active tab from the current state and switches content on route changes;
 *  - owns save-on-leave: on leaving `X.edit` it saves via the editor's lifecycle and refetches the
 *    page (no route reload — a reload would destroy this component mid-navigation).
 */
@Component({
    selector: 'wiki-container',
    template: require('./wiki.container.component.html')
})
export class WikiContainerComponent implements OnInit, OnDestroy {
    /** Parent state whose children are the tab substates (e.g. `home.project.summary.wiki.page`). */
    @Input() baseState: string = '';
    /** Which tabs to show, in order (keys: view/edit/versions/monitor/referrers/attachments). */
    @Input() tabs: string[] = ['view', 'edit', 'versions', 'monitor', 'referrers', 'attachments'];
    /** Ambient project context (not wiki-specific). */
    @Input() project: any = null;
    @Input() projectMenu: any = null;
    /** Wiki page identity — the container fetches the page itself. When empty, it reads the
     *  `pagename` route param (project wiki); hosts with a fixed page (homepages) pass it. */
    @Input() pagename: string = '';
    /** Save/versions scope. Defaults to the project. */
    @Input() section: string = 'project';
    @Input() refId: any = null;

    // ── feature toggles passed through to the content components ──
    @Input() showSidebar: boolean = true;
    @Input() showTags: boolean = true;
    @Input() showAttachments: boolean = true;
    /** Let the editor manage page title/breadcrumbs (wiki page: true; homepages: false). */
    @Input() manageChrome: boolean = true;
    /** Render the container's own tab bar. Off when the host provides its own tabs (e.g. the site
     *  admin homepage, whose tab bar also includes a non-wiki "Options" tab) — the container still
     *  reads the active tab from the route and swaps content accordingly. */
    @Input() showTabs: boolean = true;
    /** Starter content for a page that does not exist yet (no id). Empty ⇒ the wiki's generic
     *  "no content" placeholder. Hosts with a template (e.g. the sprint retrospective) pass it so
     *  a brand-new page opens pre-filled. */
    @Input() newPageContent: string = '';
    /** Right-side control buttons. */
    @Input() controls: WikiContainerControls = {};

    @ViewChild(WikiEditViewComponent, { static: false }) editView?: WikiEditViewComponent;

    wikiPage: any = null;
    lockStatus: any = null;
    wikiMonitors: any = null;
    pageMonitors: any = null;
    versionCount: number = 0;
    attachmentCount: number = 0;
    activeTab: string = 'view';

    private readonly tabDefs: { [key: string]: { key: string; label: string } } = {
        view: { key: 'view', label: marker('View') },
        edit: { key: 'edit', label: marker('Edit') },
        versions: { key: 'versions', label: marker('Versions') },
        monitor: { key: 'monitor', label: marker('Monitor') },
        referrers: { key: 'referrers', label: marker('Referrers') },
        attachments: { key: 'attachments', label: marker('Attachments') },
    };

    private deregisterSuccess: Function | null = null;
    private deregisterBefore: Function | null = null;
    private attachmentCountSub: any = null;
    /** The `[pagename]` input, if the host fixed one (homepages); empty ⇒ read from the route. */
    private fixedPagename: string = '';

    constructor(
        private stateService: StateService,
        private $transitions: TransitionService,
        private wikiResolves: WikiResolvesService,
        private wikiService: WikiService,
        private modalService: NgbModal,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        if (this.refId == null) { this.refId = _.get(this.project, 'id', 0); }
        this.fixedPagename = this.pagename;
        this.pagename = this.resolvePagename();

        this.setActiveFromState(_.get(this.stateService, 'current.name', ''));
        this.loadPage().then(() => this.loadTabData());

        // Content follows the current state; reload the page when the pagename param changes
        // (e.g. following a wiki link to another page).
        this.deregisterSuccess = this.$transitions.onSuccess({}, (transition) => {
            this.setActiveFromState(transition.to().name || '');
            const nextPage = this.resolvePagename();
            if (nextPage !== this.pagename) {
                this.pagename = nextPage;
                this.wikiPage = null;
                this.wikiMonitors = null;
                this.pageMonitors = null;
                this.loadPage().then(() => this.loadTabData());
            } else {
                this.loadTabData();
            }
        });
        // Save-on-leave — save + refetch, never reload (a reload would destroy this component).
        this.deregisterBefore = this.$transitions.onBefore(
            { exiting: this.baseState + '.edit' },
            () => this.onLeaveEdit()
        );

        this.attachmentCountSub = this.wikiService.attachmentCount$.subscribe((count: number) => {
            this.attachmentCount = count;
        });
    }

    ngOnDestroy(): void {
        if (this.deregisterSuccess) { this.deregisterSuccess(); this.deregisterSuccess = null; }
        if (this.deregisterBefore) { this.deregisterBefore(); this.deregisterBefore = null; }
        if (this.attachmentCountSub) { this.attachmentCountSub.unsubscribe(); }
    }

    // ─────────────────── derived view state ───────────────────

    get visibleTabs(): Array<{ key: string; label: string }> {
        return this.tabs.map((k) => this.tabDefs[k]).filter(Boolean);
    }

    get unixName(): string {
        return _.get(this.project, 'unixName', '');
    }

    get linkState(): string {
        return this.baseState + '.view';
    }

    get pageName(): string {
        return this.pagename;
    }

    get hasControls(): boolean {
        const c = this.controls || {};
        return !!(c.delete || c.lock || c.public || c.monitor || c.browse || c.download);
    }

    get hasUser(): boolean {
        return !!(this.project?.rel?.permissions ?? null);
    }

    /**
     * Write permission on this wiki, held by an actual session.
     *
     * A getter rather than a field set in ngOnInit so it tracks `project` if the host swaps
     * it. The hasUser() half matters: a public project hands its permission set to anonymous
     * visitors too, so the role check alone would offer editing controls to someone with no
     * account. It is how the rest of the wiki qualifies the same check — see
     * {@link WikiResolvesService.getWikiTour}.
     */
    get canEdit(): boolean {
        return this.userService.session.hasUser()
            && _.get(this.project, 'rel.permissions.wiki', 0) > 1;
    }

    /**
     * Whether the Edit tab offers the toolbar's Rename button.
     *
     * `canEdit` is the permission gate: write access on this wiki, held by a signed-in user.
     * It is presentational only — WikiService::postRename() re-checks userCanWrite() and
     * rejects the request regardless of what the UI showed.
     *
     * `!fixedPagename` rules out the hosts that pin a page name — the project admin homepage
     * and the sprint retrospective both address a specific page and would break if it moved.
     * A page with no id does not exist yet, so it has no name to change. FrontPage and
     * ProjectHome are reserved: the wiki root redirects to the first and the project homepage
     * is built on the second. The backend enforces all of this too; this only keeps the
     * control from appearing where it could never succeed.
     */
    get canRename(): boolean {
        return this.canEdit
            && !this.fixedPagename
            && !!_.get(this.wikiPage, 'id', 0)
            && ['FrontPage', 'ProjectHome'].indexOf(this.pagename) === -1;
    }

    badgeFor(key: string): number {
        if (key === 'versions') { return this.versionCount; }
        if (key === 'attachments') { return this.attachmentCount; }
        return 0;
    }

    private resolvePagename(): string {
        if (this.fixedPagename) { return this.fixedPagename; }
        const raw = _.get(this.stateService, 'params.pagename', 'FrontPage');
        return raw ? decodeURIComponent(raw) : 'FrontPage';
    }

    private setActiveFromState(stateName: string): void {
        stateName = stateName || '';
        if (stateName === this.baseState) { this.activeTab = 'view'; return; }
        const prefix = this.baseState + '.';
        if (stateName.startsWith(prefix)) {
            this.activeTab = stateName.slice(prefix.length).split('.')[0];
        }
    }

    /** Navigate to a tab's substate (updates the URL); content follows via onSuccess. */
    navigate(key: string): void {
        if (key === this.activeTab) { return; }
        const nav: any = this.stateService.go(this.baseState + '.' + key, { pagename: this.pagename });
        if (nav && typeof nav.catch === 'function') { nav.catch(() => {}); }
    }

    // ─────────────────── data ───────────────────

    /** Scope for fetching the page: project-scoped by default, section-scoped for e.g. the
     *  admin/site homepage (section !== 'project'). */
    private pageScope(): any {
        return (this.section && this.section !== 'project')
            ? { section: this.section, refId: this.refId }
            : { project: this.unixName };
    }

    private loadPage(): Promise<any> {
        return this.wikiResolves.getWikiPageScoped(this.pageScope(), this.pagename).then((wikiPage) => {
            this.seedNewPageContent(wikiPage);
            this.wikiPage = wikiPage;
            // Publish the loaded page so sibling views (e.g. the page header) can show details
            this.wikiService.wikiPage$.next(wikiPage);
            return Promise.all([this.loadLock(), this.refreshCounts()]);
        });
    }

    /** For a not-yet-created page (no id), seed the host-provided starter content so the View/Edit
     *  tabs open pre-filled instead of showing the generic empty-page placeholder. */
    private seedNewPageContent(wikiPage: any): void {
        if (!this.newPageContent) { return; }
        if (wikiPage && !wikiPage.id) {
            _.set(wikiPage, 'rel.content.data', this.newPageContent);
        }
    }

    /** Lock status is only meaningful when the Lock control is shown (project wiki page). */
    private loadLock(): Promise<any> {
        if (!this.controls || !this.controls.lock) { this.lockStatus = null; return Promise.resolve(); }
        return this.wikiResolves.getLockStatus(this.unixName, this.pagename).then((l) => this.lockStatus = l);
    }

    private refreshCounts(): Promise<any> {
        return Promise.all([
            this.wikiResolves.getVersionCount(this.wikiPage, this.project).then((c) => this.versionCount = c),
            this.wikiResolves.getAttachmentCount(this.wikiPage, this.project).then((c) => this.attachmentCount = c),
        ]);
    }

    private loadTabData(): void {
        if (this.activeTab === 'monitor' && this.wikiPage && this.wikiMonitors === null) {
            this.wikiResolves.getWikiMonitors(this.project).then((m) => this.wikiMonitors = m);
            this.wikiResolves.getPageMonitors(this.wikiPage).then((m) => this.pageMonitors = m);
        }
    }

    // ─────────────────── save-on-leave ───────────────────

    private onLeaveEdit(): boolean | Promise<any> {
        const ev = this.editView;
        if (!ev) { return true; }
        // Flush through the editor's lifecycle
        const hadWork = ev.hasPendingWork();
        return ev.wikiEditorLifecycle.flushPendingSave().then((ok: boolean) => {
            if (!ok) { return false; }
            // Refetch whenever anything was persisted during this Edit session: work flushed just
            // now, or an earlier save via the editor's Save button / Ctrl+S. 
            const saved = ev.wikiEditorLifecycle.consumeSavedSinceLoad();
            return (hadWork || saved) ? this.loadPage().then(() => true) : true;
        });
    }

    // ─────────────────── controls ───────────────────

    /**
     * Rename the current page.
     *
     */
    onRenameRequested(): void {
        const ev = this.editView;
        const pending = ev ? ev.wikiEditorLifecycle.flushPendingSave() : Promise.resolve(true);

        pending.then((ok: boolean) => {
            if (!ok) { return; }
            this.openRenameModal();
        });
    }

    private openRenameModal(): void {
        const modalRef = this.modalService.open(WikiRenameModalComponent);
        modalRef.componentInstance.wiki = this.wikiPage;
        modalRef.componentInstance.project = this.project;
        modalRef.componentInstance.onRenamed = (report: any) => {
            const renamed = _.get(report, 'pagename', '') || this.pagename;
            // Move to the new URL in place, so Back does not return to a name that no longer
            // resolves. The onSuccess hook sees the changed param and reloads the page, which
            // also refreshes the heading and breadcrumbs.
            const nav: any = this.stateService.go(
                this.baseState + '.edit',
                { pagename: renamed },
                { location: 'replace' }
            );
            if (nav && typeof nav.catch === 'function') { nav.catch(() => {}); }
        };
        modalRef.componentInstance.onClose = () => modalRef.close();
    }

    deleteWikiPage(): void {
        const modalRef = this.modalService.open(WikiDeleteModalComponent);
        modalRef.componentInstance.wiki = this.wikiPage;
        modalRef.componentInstance.project = this.project;
        modalRef.componentInstance.onDelete = () => {
            if (this.controls.browseState) {
                this.stateService.go(this.controls.browseState);
            }
        };
        modalRef.componentInstance.onClose = () => modalRef.close();
    }
}
